import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Loader2, X } from 'lucide-react';
import { chronologyApi } from '../../services/chronologyApi';

export const EVENT_TYPE_OPTIONS = [
  { value: 'hospital_visit', label: 'Hospital Visit' },
  { value: 'outpatient_appointment', label: 'Outpatient' },
  { value: 'emergency_visit', label: 'Emergency' },
  { value: 'surgical_episode', label: 'Surgery' },
  { value: 'treatment_course', label: 'Treatment' },
  { value: 'admission', label: 'Admission' },
  { value: 'discharge', label: 'Discharge' },
  { value: 'diagnosis', label: 'Diagnosis' },
  { value: 'major_procedure', label: 'Procedure' },
  { value: 'medication_routine', label: 'Medication' },
  { value: 'medication', label: 'Medication (General)' },
  { value: 'vital_signs', label: 'Vitals' },
  { value: 'lab_result', label: 'Lab Result' },
  { value: 'consultation', label: 'Consultation' },
  { value: 'imaging_study', label: 'Imaging' },
  { value: 'observation', label: 'Observation' },
  { value: 'investigation', label: 'Investigation' },
  { value: 'procedure', label: 'Procedure (General)' },
  { value: 'administrative', label: 'Administrative' },
  { value: 'gp_visit', label: 'GP Visit' },
  { value: 'gp_telephone_consultation', label: 'GP Phone' },
  { value: 'prescription_renewal', label: 'Prescription' },
  { value: 'physiotherapy', label: 'Physiotherapy' },
  { value: 'mental_health_review', label: 'Mental Health' },
  { value: 'referral_letter', label: 'Referral' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'expert_examination', label: 'Expert Examination' },
  { value: 'expert_opinion', label: 'Expert Opinion' },
  { value: 'other', label: 'General' },
];

const CORRECTION_REASONS = [
  { id: 'additional_context', label: 'Additional context' },
  { id: 'inaccurate_extraction', label: 'Inaccurate extraction' },
  { id: 'wrong_date', label: 'Wrong date' },
  { id: 'wrong_category', label: 'Wrong category' },
  { id: 'missing_detail', label: 'Missing detail' },
  { id: 'duplicate_event', label: 'Duplicate event' },
];

export interface EditedFields {
  headline?: string;
  summary?: string;
  description?: string;
  eventDate?: string;
  eventType?: string;
}

interface CorrectionReasonModalProps {
  eventId: string;
  caseId: string;
  changes: EditedFields;
  onClose: () => void;
  onSaved: () => void;
}

export const CorrectionReasonModal: React.FC<CorrectionReasonModalProps> = ({
  eventId,
  caseId,
  changes,
  onClose,
  onSaved,
}) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const correctionReason = selectedReason
    ? CORRECTION_REASONS.find(r => r.id === selectedReason)!.label +
      (customReason.trim() ? `: ${customReason.trim()}` : '')
    : customReason.trim();

  const mutation = useMutation(
    (payload: Parameters<typeof chronologyApi.correctEvent>[1]) =>
      chronologyApi.correctEvent(eventId, payload),
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(['timeline', caseId]);
        onSaved();
      },
      onError: (err: any) => {
        setError(err?.message || 'Failed to save correction');
      },
    }
  );

  const handleSubmit = () => {
    setError(null);
    mutation.mutate({ ...changes, correctionReason });
  };

  const canSubmit = correctionReason.length > 0 && !mutation.isLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 dark:bg-black/60" />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 rounded-xl border border-medical-200 dark:border-medical-600 bg-white dark:bg-medical-800 shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-medical-900 dark:text-medical-100">
            Why are you correcting this event?
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-medical-400 hover:text-medical-600 dark:hover:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {CORRECTION_REASONS.map((reason) => (
            <button
              key={reason.id}
              type="button"
              onClick={() =>
                setSelectedReason((prev) => (prev === reason.id ? null : reason.id))
              }
              className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                selectedReason === reason.id
                  ? 'border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'border-medical-200 dark:border-medical-600 text-medical-600 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700'
              }`}
            >
              {reason.label}
            </button>
          ))}
        </div>

        <textarea
          value={customReason}
          onChange={(e) => setCustomReason(e.target.value)}
          placeholder={selectedReason ? 'Add details (optional)' : 'Or describe the reason...'}
          maxLength={500}
          rows={2}
          className="w-full rounded-lg border border-medical-200 dark:border-medical-600 bg-white dark:bg-medical-900 px-3 py-2 text-sm text-medical-900 dark:text-medical-100 placeholder-medical-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors resize-none"
        />

        {error && (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={mutation.isLoading}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-medical-600 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Save correction
          </button>
        </div>
      </div>
    </div>
  );
};
